import type { DefectFeaturesResponseBody } from '../contracts/defectFeatures';

export interface RecipeFeatureFilter {
  readonly bandSlugs?: readonly string[];
  readonly minValue?: number;
  readonly maxValue?: number;
}

export function hasRecipeFeatureFilter(
  filter: RecipeFeatureFilter | null | undefined,
): filter is RecipeFeatureFilter {
  if (!filter) {
    return false;
  }
  return (
    (filter.bandSlugs != null && filter.bandSlugs.length > 0) ||
    (typeof filter.minValue === 'number' && Number.isFinite(filter.minValue)) ||
    (typeof filter.maxValue === 'number' && Number.isFinite(filter.maxValue))
  );
}

function matchesBand(
  band: string | undefined,
  bandSlugs: readonly string[] | undefined,
): boolean {
  if (!bandSlugs || bandSlugs.length === 0) {
    return true;
  }
  if (typeof band !== 'string') {
    return false;
  }
  const normalized = band.trim().toLowerCase();
  return bandSlugs.some((slug) => slug.trim().toLowerCase() === normalized);
}

function matchesValue(
  value: number | undefined,
  minValue: number | undefined,
  maxValue: number | undefined,
): boolean {
  const hasMin = typeof minValue === 'number' && Number.isFinite(minValue);
  const hasMax = typeof maxValue === 'number' && Number.isFinite(maxValue);
  if (!hasMin && !hasMax) {
    return true;
  }
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return false;
  }
  if (hasMin && value < (minValue as number)) {
    return false;
  }
  if (hasMax && value > (maxValue as number)) {
    return false;
  }
  return true;
}

export function featureMatchesRecipeFilter(
  properties: {
    readonly band?: string;
    readonly value?: number;
  } | null | undefined,
  filter: RecipeFeatureFilter,
): boolean {
  if (!properties) {
    return false;
  }
  if (!matchesBand(properties.band, filter.bandSlugs)) {
    return false;
  }
  return matchesValue(properties.value, filter.minValue, filter.maxValue);
}

export function filterFeaturesByRecipeFilter(
  collection: DefectFeaturesResponseBody,
  filter: RecipeFeatureFilter | null | undefined,
): DefectFeaturesResponseBody {
  if (!hasRecipeFeatureFilter(filter)) {
    return collection;
  }

  return {
    type: 'FeatureCollection',
    features: collection.features.filter((feature) =>
      featureMatchesRecipeFilter(feature.properties, filter),
    ),
  };
}
